import { shareOnScale, type ValueScale } from '../ColumnChart/chart-scale.ts';
import { positionOf } from '../Chart/BucketLabels.tsx';
import type { ChartSeries } from '../Chart/chart-series.ts';

export const VIEW_SIZE = 1000;

type Placed = { readonly x: number; readonly y: number };

type Segment = readonly Placed[];

const segmentsOf = (series: ChartSeries, scale: ValueScale): readonly Segment[] => {
  const count = series.points.length;
  return series.points
    .reduce<readonly Segment[]>(
      (segments, point, index) => {
        if (point.missing === true) return [...segments, []];
        const placed = {
          x: positionOf(index, count) * VIEW_SIZE,
          y: (1 - shareOnScale(scale, point.value)) * VIEW_SIZE,
        };
        const open = segments.at(-1) ?? [];
        return [...segments.slice(0, -1), [...open, placed]];
      },
      [[]],
    )
    .filter((segment) => segment.length > 0);
};

const pathOf = (segment: Segment): string =>
  segment
    .map((placed, index) => `${index === 0 ? 'M' : 'L'} ${String(placed.x)} ${String(placed.y)}`)
    .join(' ');

export const linePathOf = (series: ChartSeries, scale: ValueScale): string =>
  segmentsOf(series, scale).map(pathOf).join(' ');

export const areaPathOf = (series: ChartSeries, scale: ValueScale): string => {
  const floor = (1 - shareOnScale(scale, 0)) * VIEW_SIZE;
  return segmentsOf(series, scale)
    .map((segment) => {
      const first = segment[0]?.x ?? 0;
      const last = segment.at(-1)?.x ?? 0;
      return `${pathOf(segment)} L ${String(last)} ${String(floor)} L ${String(first)} ${String(floor)} Z`;
    })
    .join(' ');
};
